import { ClerkProvider } from "@clerk/clerk-react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ErrorBoundary } from "@/app/_/config/ErrorBoundary";
import { Snackbar } from "@/app/_/config/Snackbar";
import { ThemeRegistry } from "@/app/_/config/ThemeRegistry";
import { NodeDetailLayout } from "@/app/app/_/layouts/NodeDetailLayout";
import { AppFallbackRedirect, AppIndexRedirect } from "@/app/app/_/routing";
import { ChartOfAccountsView } from "@/app/app/_/views/ChartOfAccountsView";
import { ContactsView } from "@/app/app/_/views/ContactsView";
import { DocumentsView } from "@/app/app/_/views/DocumentsView";
import { FinancialAccountsView } from "@/app/app/_/views/FinancialAccountsView";
import { InvitationsView } from "@/app/app/_/views/InvitationsView";
import { ClientsView } from "@/app/app/_/views/ClientsView";
import { SettingsAccountantView } from "@/app/app/_/views/SettingsView/SettingsAccountantView";
import { SettingsEmailsView } from "@/app/app/_/views/SettingsView/SettingsEmailsView";
import { SettingsEntityGate } from "@/app/app/_/views/SettingsView/SettingsEntityGate";
import { SettingsEntityView } from "@/app/app/_/views/SettingsView/SettingsEntityView";
import { SettingsIndexRedirect } from "@/app/app/_/views/SettingsView/SettingsIndexRedirect";
import { SettingsProfileView } from "@/app/app/_/views/SettingsView/SettingsProfileView";
import { JournalEntriesView } from "@/app/app/_/views/JournalEntriesView";
import { OrganizationsView } from "@/app/app/_/views/OrganizationsView";
import { TaxCodesView } from "@/app/app/_/views/TaxCodesView";
import { ThreadView } from "@/app/app/_/views/ThreadView";
import { TransactionsView } from "@/app/app/_/views/TransactionsView";
import { TriageView } from "@/app/app/_/views/TriageView";
import { BudgetView } from "@/app/app/_/views/BudgetView";
import { Values } from "@/constants/values";
import { ReportsView } from "@/app/app/_/views/ReportsView";
import { RulesView } from "@/app/app/_/views/RulesView";

// Page imports
import AppIntegrationsPage from "@/app/app/integrations/page";
import AppPage from "@/app/app/page";
import SignInPage from "@/app/auth/sign-in/[[...sign-in]]/page";
import SignUpPage from "@/app/auth/sign-up/[[...sign-up]]/page";
import WaitlistPage from "@/app/auth/waitlist/page";
import BrushPage from "@/app/brush/page";
import StylesPage from "@/app/dev/styles/page";
import CompanyAboutPage from "@/app/company/about/page";
import CompanyCareersPage from "@/app/company/careers/page";
import CompanyContactPage from "@/app/company/contact/page";
import CompanyInvestmentPage from "@/app/company/investment/page";
import HomePage from "@/app/page";
import PlatformPage from "@/app/platform/page";
import PlatformAgentsPage from "@/app/platform/agents/page";
import PlatformLedgerPage from "@/app/platform/ledger/page";
import PlatformMultiEntityPage from "@/app/platform/multi-entity/page";
import PlatformReportsPage from "@/app/platform/reports/page";
import PlatformTriagePage from "@/app/platform/triage/page";
import PolicyPage from "@/app/policies/[id]/page";
import PricingPage from "@/app/pricing/page";

/** The story carries anime.js, so it loads with its own page rather than with every other. */
const StoryPage = lazy(() => import("@/app/story/page"));
import InvitationPage from "@/app/invitation/[kind]/[id]/page";
import PublicIdPage from "@/app/public/[id]/page";
import SharedReportPage from "@/app/share/report/[token]/page";
import ResourcesFaqPage from "@/app/resources/faq/page";
import ResourcesHelpPage from "@/app/resources/help/page";
import ResourcesSupportPage from "@/app/resources/support/page";
import SolutionsPage from "@/app/solutions/page";
import SolutionsFinanceOpsPage from "@/app/solutions/finance-ops/page";
import SolutionsFoundersPage from "@/app/solutions/founders/page";
import SolutionsMultiEntityOperatorsPage from "@/app/solutions/multi-entity-operators/page";
import SolutionsSmallBusinessesPage from "@/app/solutions/small-businesses/page";

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <Routes>
        {/* Public-facing routes rendered outside ThemeRegistry/AuthSync */}
        <Route path="/public/:id" element={<PublicIdPage />} />
        <Route
          path="/share/report/:token"
          element={
            <ThemeRegistry styleTheme="rounded">
              <SharedReportPage />
            </ThemeRegistry>
          }
        />

        {/* All other routes go through ThemeRegistry (AuthSync + Apollo) */}
        <Route
          path="*"
          element={
            <ThemeRegistry>
              <main>
                <Snackbar />
                <ErrorBoundary>
                  <Suspense>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/story" element={<StoryPage />} />
                      <Route path="/policies/:id" element={<PolicyPage />} />
                      <Route path="/platform" element={<PlatformPage />} />
                      <Route
                        path="/platform/agents"
                        element={<PlatformAgentsPage />}
                      />
                      <Route
                        path="/platform/triage"
                        element={<PlatformTriagePage />}
                      />
                      <Route
                        path="/platform/reports"
                        element={<PlatformReportsPage />}
                      />
                      <Route
                        path="/platform/multi-entity"
                        element={<PlatformMultiEntityPage />}
                      />
                      <Route
                        path="/platform/ledger"
                        element={<PlatformLedgerPage />}
                      />
                      <Route
                        path="/platform/bookkeeping"
                        element={<Navigate to="/platform/ledger" replace />}
                      />
                      <Route
                        path="/platform/payments"
                        element={<Navigate to="/platform/ledger" replace />}
                      />
                      <Route path="/solutions" element={<SolutionsPage />} />
                      <Route
                        path="/solutions/multi-entity-operators"
                        element={<SolutionsMultiEntityOperatorsPage />}
                      />
                      <Route
                        path="/solutions/finance-ops"
                        element={<SolutionsFinanceOpsPage />}
                      />
                      <Route
                        path="/solutions/founders"
                        element={<SolutionsFoundersPage />}
                      />
                      <Route
                        path="/solutions/small-businesses"
                        element={<SolutionsSmallBusinessesPage />}
                      />
                      <Route
                        path="/solutions/startups"
                        element={<Navigate to="/solutions/founders" replace />}
                      />
                      <Route
                        path="/solutions/freelancers"
                        element={
                          <Navigate to="/solutions/finance-ops" replace />
                        }
                      />
                      <Route
                        path="/solutions/agencies"
                        element={
                          <Navigate
                            to="/solutions/multi-entity-operators"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/resources/help"
                        element={<ResourcesHelpPage />}
                      />
                      <Route
                        path="/resources/faq"
                        element={<ResourcesFaqPage />}
                      />
                      <Route
                        path="/resources/support"
                        element={<ResourcesSupportPage />}
                      />
                      <Route
                        path="/company/about"
                        element={<CompanyAboutPage />}
                      />
                      <Route
                        path="/company/careers"
                        element={<CompanyCareersPage />}
                      />
                      <Route
                        path="/company/contact"
                        element={<CompanyContactPage />}
                      />
                      <Route
                        path="/company/investment"
                        element={<CompanyInvestmentPage />}
                      />

                      {/* Auth routes */}
                      <Route path="/auth/sign-in/*" element={<SignInPage />} />
                      <Route path="/auth/sign-up/*" element={<SignUpPage />} />
                      <Route path="/auth/waitlist" element={<WaitlistPage />} />
                      {/* What an invitation's email opens, signed out or in. */}
                      <Route
                        path="/invitation/books/:id"
                        element={<InvitationPage kind="books" />}
                      />
                      <Route
                        path="/invitation/business/:id"
                        element={<InvitationPage kind="business" />}
                      />

                      {/* App routes */}
                      <Route path="/app" element={<AppPage />}>
                        <Route index element={<AppIndexRedirect />} />
                        <Route
                          path="overview"
                          element={<Navigate to="/app/reports" replace />}
                        />
                        <Route
                          path="views"
                          element={<Navigate to="/app/reports" replace />}
                        />
                        <Route
                          path="reports/cash-flow"
                          element={<Navigate to="/app/reports" replace />}
                        />
                        <Route
                          path="reports/profit-loss"
                          element={<Navigate to="/app/reports" replace />}
                        />
                        <Route
                          path="reports/balance-sheet"
                          element={<Navigate to="/app/reports" replace />}
                        />
                        <Route path="threads" element={<ThreadView />} />
                        <Route path="rules" element={<RulesView />} />
                        <Route
                          path="workflows"
                          element={<Navigate to="/app/rules" replace />}
                        />
                        <Route path="triage" element={<TriageView />} />
                        {Values.IS_BUDGET_SHOWN && (
                          <Route path="budget" element={<BudgetView />} />
                        )}
                        <Route path="reports" element={<ReportsView />} />
                        {/* A list and its records' pages share one route, so
                            the list stays mounted (hidden) under a record's
                            page — see ``NodeDetailLayout``. */}
                        <Route
                          path="transactions/:tab/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <TransactionsView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="financial-accounts/:tab/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <FinancialAccountsView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="chart-of-accounts"
                          element={<ChartOfAccountsView />}
                        />
                        <Route
                          path="documents/:tab/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <DocumentsView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="contacts/:tab/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <ContactsView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="organizations/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <OrganizationsView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="tax-codes/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <TaxCodesView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="journal-entries/:identifier?"
                          element={
                            <NodeDetailLayout>
                              <JournalEntriesView />
                            </NodeDetailLayout>
                          }
                        />
                        <Route
                          path="invitations"
                          element={<InvitationsView />}
                        />
                        <Route path="clients" element={<ClientsView />} />
                        <Route path="settings">
                          <Route index element={<SettingsIndexRedirect />} />
                          <Route
                            path="profile"
                            element={<SettingsProfileView />}
                          />
                          <Route
                            path="emails"
                            element={<SettingsEmailsView />}
                          />
                          <Route
                            path="accountant"
                            element={<SettingsAccountantView />}
                          />
                          <Route
                            path="entity/:identifier"
                            element={<SettingsEntityGate />}
                          >
                            <Route index element={<SettingsEntityView />} />
                            <Route
                              path="members"
                              element={<SettingsEntityView />}
                            />
                            <Route
                              path="roles"
                              element={<SettingsEntityView />}
                            />
                            <Route
                              path="clients"
                              element={<SettingsEntityView />}
                            />
                            <Route
                              path="billing"
                              element={<SettingsEntityView />}
                            />
                            <Route
                              path="integrations"
                              element={<SettingsEntityView />}
                            />
                            <Route
                              path="templates"
                              element={<SettingsEntityView />}
                            />
                          </Route>
                        </Route>
                        <Route path="*" element={<AppFallbackRedirect />} />
                      </Route>
                      <Route
                        path="/app/integrations"
                        element={<AppIntegrationsPage />}
                      />

                      {/* Dev routes */}
                      <Route path="/dev/styles" element={<StylesPage />} />
                      <Route path="/brush" element={<BrushPage />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </main>
            </ThemeRegistry>
          }
        />
      </Routes>
    </ClerkProvider>
  );
}
